'use client';

import { Image } from '@heroui/react';
import { BiEnvelope, BiPhone, BiSend, BiTime } from 'react-icons/bi';
import CompactRepairItem from './CompactRepairItem';
import { formatPhoneForDisplay } from '@/util/functions/formatPhoneForDisplay';
import { SPECIALTY_BRAND } from './specialtyBrand';
import type { QuickFixRepairItem, QuickFixSpecialtyContractor } from './types';

export default function SpecialtyContractorCard({
  contractor,
  items,
  sent,
  onSend,
  sendDisabled: sendDisabledProp = false,
}: {
  contractor: QuickFixSpecialtyContractor;
  items: QuickFixRepairItem[];
  sent: boolean;
  onSend: () => void;
  sendDisabled?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <div
      className='relative isolate mb-2.5 overflow-hidden rounded-[10px] border border-[#fde7b8]/60 p-3.5 text-foreground shadow-[0_4px_20px_-6px_rgba(245,165,36,0.06)] transition-[border-color,box-shadow] hover:border-[#fbd784]/80 hover:shadow-[0_6px_22px_-6px_rgba(245,165,36,0.1)]'
      style={{ background: SPECIALTY_BRAND.cardGradient }}
    >
      <div
        className='pointer-events-none absolute inset-0 z-0 opacity-30'
        style={{
          backgroundImage:
            'radial-gradient(circle at 18% 28%, rgba(245,165,36,0.04) 0 2px, transparent 2px), radial-gradient(circle at 82% 72%, rgba(253,231,184,0.05) 0 2px, transparent 2px)',
          backgroundSize: '24px 24px',
        }}
        aria-hidden
      />
      <div
        className='pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(115%_90%_at_100%_0%,rgba(255,248,235,0.35),transparent_55%),radial-gradient(85%_100%_at_0%_100%,rgba(253,231,184,0.12),transparent_60%)]'
        aria-hidden
      />

      <div className='relative z-10'>
      <div className='flex items-start gap-3'>
        {contractor.logoUrl ? (
          <Image
            src={contractor.logoUrl}
            alt={contractor.name}
            className='h-[38px] w-[38px] shrink-0 rounded-md object-contain'
            radius='sm'
          />
        ) : (
          <div
            className='grid h-[38px] w-[38px] shrink-0 place-items-center rounded-md font-mono text-[13px] font-bold text-white'
            style={{ background: contractor.color }}
          >
            {contractor.initials}
          </div>
        )}
        <div className='min-w-0 flex-1'>
          <div className='mb-1 flex flex-wrap items-center gap-2'>
            <h4 className='shrink-0 font-mono text-sm font-bold tracking-tight text-foreground'>
              {contractor.name}
            </h4>
            <span className='whitespace-nowrap rounded-full bg-[#ffe1e1] px-2 py-0.5 text-[10.5px] font-bold text-[#c62828]'>
              {items.length} New {items.length === 1 ? 'Item' : 'Items'}
            </span>
          </div>
          <div className='mt-0.5 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-default-500'>
            <span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
              <BiEnvelope className='shrink-0' size={13} />
              {contractor.email}
            </span>
            <span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
              <BiPhone className='shrink-0' size={13} />
              {formatPhoneForDisplay(contractor.phone) || contractor.phone}
            </span>
          </div>
          <div className='mt-1.5'>
            <span className='inline-flex items-center gap-1 rounded-full bg-[#fff3da] px-2 py-0.5 text-[11px] font-semibold text-[#8a5a00]'>
              <BiTime size={11} />
              {contractor.responseTime}
            </span>
          </div>
        </div>
        <button
          type='button'
          onClick={onSend}
          disabled={sent || sendDisabledProp}
          aria-label={
            sent
              ? `Quote sent to ${contractor.name}`
              : `Send quote to ${contractor.name}`
          }
          className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-[11.5px] font-bold uppercase tracking-wide transition ${
            sent
              ? 'cursor-default bg-themeLightGreen text-[#1f6b3f]'
              : 'cursor-pointer bg-[#fff3da] text-[#8a5a00] hover:bg-[#fde7b8]'
          }`}
        >
          {sent ? <>✓ Sent</> : <><BiSend size={14} /> Send Quote</>}
        </button>
      </div>

      <div className='mt-3 flex flex-wrap items-center gap-1.5 text-xs'>
        <span className='mr-1 font-semibold text-foreground'>Specialties:</span>
        {contractor.specialties.map((s) => (
          <span
            key={s}
            className='whitespace-nowrap rounded-full bg-[#f1f3f5] px-2 py-0.5 text-[11.5px] font-medium text-[#495057]'
          >
            {s}
          </span>
        ))}
      </div>

      <div className='mt-2.5 rounded-lg border border-[#fde7b8]/30 bg-white/50 px-2.5 pb-2 pt-2.5'>
        <p className='mb-1.5 text-xs font-bold text-[#8a5a00]'>New Items to Quote:</p>
        <div className='space-y-0'>
          {items.map((it) => (
            <CompactRepairItem key={it.id} item={it} />
          ))}
        </div>
      </div>
      </div>
    </div>
  );
}
